import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOnboarding } from '../../contexts/OnboardingContext';
import Icon from './Icon';
import CloseButton from './CloseButton';
import Modal from './Modal';

/**
 * OnboardingProgress - Shows user progress through key app milestones
 *
 * A checklist of the first things worth doing in the app. Two shapes:
 * - 'row': one line on Home ("Getting started · 2 of 7 · Next: …") with a
 *   progress bar and an ✕; tapping it opens the full list in a bottom sheet.
 *   It replaced the full list on Home (2026-10-01), which ran ~750px on a
 *   phone with no way to close it.
 * - 'full': the whole list (the sheet, and Settings → Help).
 */
function OnboardingProgress({
  variant = 'full', // 'row' | 'full'
  showTitle = true,
  onDismiss,
  // 'full' only: called when a step's action link is followed (the sheet
  // closes itself through this, since some links stay on the same page)
  onStepClick
}) {
  const { t } = useTranslation('onboarding');
  const [sheetOpen, setSheetOpen] = useState(false);
  const { hasMilestone, userLevel, hasHomeLocation, followCount, isLoading } = useOnboarding();

  // Step key stems + non-translatable icon/action data. Title, description,
  // and actionLabel text live in src/locales/en/onboarding.json under
  // progress.steps.<key>.
  // The first two aren't milestones — they're account state the /welcome flow
  // captures, and they lead the list because they're what make everything
  // below them useful (local events need a location; alerts need a follow).
  // `done` overrides the milestone lookup for those; the rest fall through to
  // hasMilestone as before.
  const steps = [
    { key: 'setHomeLocation', icon: 'mapPin', action: '/welcome?returnTo=/', done: hasHomeLocation === true },
    { key: 'firstFollow', icon: 'star', action: '/settings?tab=following', done: (followCount || 0) > 0 },
    { key: 'firstSearch', icon: 'search', action: '/' },
    { key: 'visitedDiscover', icon: 'sparkles', action: '/social-feed' },
    { key: 'firstSave', icon: 'bookmark', action: '/social-feed' },
    { key: 'visitedPlanner', icon: 'calendar', action: '/activity-planner' },
    { key: 'firstTripCreated', icon: 'airplane', action: '/trip/new' }
  ];

  const isStepDone = (step) =>
    (typeof step.done === 'boolean' ? step.done : hasMilestone(step.key));

  // Nothing is known until onboarding state resolves, and rendering early
  // flashes "Set your home location" as incomplete at users who have one.
  if (isLoading) return null;

  // Calculate completion
  const completedCount = steps.filter(isStepDone).length;
  const totalSteps = steps.length;
  const progressPercent = Math.round((completedCount / totalSteps) * 100);
  const isComplete = completedCount === totalSteps;

  // Don't show if user is experienced (completed all milestones)
  if (isComplete && variant !== 'full') {
    return null;
  }

  // Get level badge using design system tokens; label text lives in
  // progress.levelBadges.<userLevel>
  const levelBadge = { label: t(`progress.levelBadges.${userLevel}`) };

  // Row variant - one line on Home that opens the full list in a sheet
  if (variant === 'row') {
    const nextStep = steps.find(s => !isStepDone(s));
    if (!nextStep) return null;

    return (
      <section
        aria-label={t('progress.row.heading')}
        className="bg-surface rounded-card border border-border overflow-hidden"
      >
        <div className="flex items-center">
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="flex-1 min-w-0 flex items-center gap-3 pl-4 pr-1 py-3 text-left hover:bg-surface-secondary transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-light"
          >
            <span className="flex-1 min-w-0">
              <span className="block font-semibold text-content-primary">{t('progress.row.heading')}</span>
              <span className="block text-sm text-content-secondary truncate">
                <span className="tabular-nums">{t('progress.row.count', { completed: completedCount, total: totalSteps })}</span>
                {t('progress.row.separator')}
                {t('progress.row.next', { step: t(`progress.steps.${nextStep.key}.title`) })}
              </span>
            </span>
            <Icon name="chevronRight" className="w-4 h-4 text-content-muted flex-shrink-0" />
          </button>
          {onDismiss && (
            <CloseButton
              variant="plain"
              size="sm"
              onClick={onDismiss}
              ariaLabel={t('progress.row.dismissAriaLabel')}
              className="mr-1"
            />
          )}
        </div>
        <div className="h-1 bg-surface-tertiary" aria-hidden="true">
          <div
            className="h-full bg-primary transition-all duration-slower"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        <Modal
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          position="bottom"
          showCloseButton
          ariaLabel={t('progress.row.heading')}
        >
          <div className="p-4 pt-5">
            <h2 className="font-display font-medium text-xl text-content-primary mb-3 pr-8">{t('progress.row.heading')}</h2>
            <OnboardingProgress variant="full" showTitle={false} onStepClick={() => setSheetOpen(false)} />
          </div>
        </Modal>
      </section>
    );
  }

  // Full variant - complete checklist with descriptions. A section on the
  // page since the 2026-10-01 refresh: serif heading + plain progress bar,
  // then ONE list frame of steps (it was a gradient-headed card holding a
  // green box per finished step — a card full of cards).
  return (
    <section aria-labelledby={showTitle ? 'onboarding-progress-heading' : undefined}>
      {showTitle && (
      <div className="flex items-end justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h3 id="onboarding-progress-heading" className="font-display font-medium text-xl text-content-primary">{t('progress.full.heading')}</h3>
          <p className="text-content-secondary text-sm mt-0.5">
            {isComplete
              ? t('progress.full.allDoneSubheading')
              : t('progress.full.stepsRemaining', { count: totalSteps - completedCount })
            }
          </p>
        </div>
        <div className="flex-shrink-0 flex items-center gap-2">
          {/* Gold: recognition — the level is an achievement */}
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-accent-light text-primary-dark">
            {levelBadge.label}
          </span>
          <span className="font-display font-medium text-2xl text-content-primary tabular-nums">{progressPercent}%</span>
        </div>
      </div>
      )}

      {/* Progress bar */}
      <div className="h-1.5 bg-surface-tertiary rounded-full overflow-hidden mb-4" aria-hidden="true">
        <div
          className="h-full bg-primary rounded-full transition-all duration-slower"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Steps */}
      <ul className="bg-surface rounded-card border border-border divide-y divide-border overflow-hidden">
        {steps.map((step, index) => {
          const isCompleted = isStepDone(step);

          return (
            <li key={step.key} className="flex items-start gap-3 p-4">
              {/* Checkbox */}
              <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                isCompleted
                  ? 'bg-success text-content-inverse'
                  : 'bg-surface border-2 border-border'
              }`}>
                {isCompleted ? (
                  <Icon name="check" className="w-4 h-4" />
                ) : (
                  <span className="text-xs text-content-muted tabular-nums">{index + 1}</span>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Icon name={step.icon} className={`w-4 h-4 flex-shrink-0 ${isCompleted ? 'text-content-muted' : 'text-content-secondary'}`} />
                  <span className={`font-semibold ${isCompleted ? 'text-content-secondary' : 'text-content-primary'}`}>
                    {t(`progress.steps.${step.key}.title`)}
                  </span>
                </div>
                <p className={`text-sm mt-0.5 ${isCompleted ? 'text-content-muted' : 'text-content-secondary'}`}>
                  {t(`progress.steps.${step.key}.description`)}
                </p>
              </div>

              {/* Action (only for incomplete) */}
              {!isCompleted && (
                <Link
                  to={step.action}
                  onClick={onStepClick}
                  className="flex-shrink-0 self-center px-3 h-9 inline-flex items-center text-sm font-semibold text-primary bg-surface border border-border rounded-lg hover:bg-surface-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light"
                >
                  {t(`progress.steps.${step.key}.actionLabel`)}
                </Link>
              )}
            </li>
          );
        })}
      </ul>

      {/* Footer */}
      {isComplete && (
        <div className="mt-3 flex items-center gap-2 text-success-dark">
          <Icon name="party" className="w-5 h-5" />
          <span className="font-medium">{t('progress.full.footerMessage')}</span>
        </div>
      )}
    </section>
  );
}

export default OnboardingProgress;
